import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { get } from '@ember/helper';
import { service } from '@ember/service';
import type Intl from 'ember-intl/services/intl';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';

const defaultIntervalMs = 700;

export interface TioLoadingMessagesSignature {
  Args: {
    /**
     * The root path to all translation keys used by this component.
     */
    i18nPath?: string;

    /**
     * The messages to display. Either this or `@messageKeys` must be provided.
     */
    messages?: string[];

    /**
     * The translation keys for the messages to display. Either this or
     * `@messages` must be provided. If `@i18nPath` is provided, the keys will be
     * scoped to this path.
     */
    messageKeys?: string[];

    /**
     * The number of milliseconds before the first message is displayed.
     * Defaults to `3500`.
     */
    initialIntervalMs?: number;

    /**
     * The number of milliseconds to display each message before switching to the
     * next one.
     */
    messageIntervalMs?: number;
  };
  Element: HTMLElement;
}

/**
 * Rotates messages starting initialIntervalMs milliseconds after mounting and stops when unmounted.
 * A surrounding container controls positioning.
 */
export default class TioLoadingMessagesComponent extends Component<TioLoadingMessagesSignature> {
  @service declare intl: Intl;

  timerId?: ReturnType<typeof setTimeout>;

  @tracked index = -1;

  constructor(owner: Owner, args: TioLoadingMessagesSignature['Args']) {
    super(owner, args);

    registerDestructor(this, () => {
      this.stop();
    });

    this.start();
  }

  get messages() {
    const { messageKeys = [], messages, i18nPath } = this.args;

    return messages
      ? messages
      : messageKeys.map((key: string) => this.intl.t(i18nPath ? `${i18nPath}.${key}` : key));
  }

  get showingMessages() {
    return this.index >= 0;
  }

  rotateMessage() {
    this.index = (this.index + 1) % this.messages.length;

    this.timerId = setTimeout(
      () => this.rotateMessage(),
      this.args.messageIntervalMs ?? defaultIntervalMs
    );
  }

  start = () => {
    this.timerId = setTimeout(
      () => this.rotateMessage(),
      this.args.initialIntervalMs ?? defaultIntervalMs
    );
  };

  stop = () => {
    clearTimeout(this.timerId);
  };

  <template>
    <div>
      {{get this.messages this.index}}
    </div>
  </template>
}
